import * as React from 'react'

type Notification = string | null

export interface NotificationState {
  error: Notification
  success: Notification
  info: Notification
  warning: Notification
}

type SuccessNotificationAction = {
  type: 'SetSuccess'
  payload: string
}

type ClearSuccessNotificationAction = {
  type: 'ClearSuccess'
}

type ErrorNotificationAction = {
  type: 'SetError'
  payload: string
}

type ClearErrorNotificationAction = {
  type: 'ClearError'
}

type InfoNotificationAction = {
  type: 'SetInfo'
  payload: string
}

type ClearInfoNotificationAction = {
  type: 'ClearInfo'
}

type WarningNotificationAction = {
  type: 'SetWarning'
  payload: string
}

type ClearWarningNotificationAction = {
  type: 'ClearWarning'
}

type ClearAllAction = {
  type: 'ClearAll'
}

export type NotificationAction =
  | SuccessNotificationAction
  | ErrorNotificationAction
  | InfoNotificationAction
  | WarningNotificationAction
  | ClearWarningNotificationAction
  | ClearInfoNotificationAction
  | ClearErrorNotificationAction
  | ClearSuccessNotificationAction
  | ClearAllAction

export interface NotificationContextState {
  state: NotificationState
  dispatch: (action: NotificationAction) => void
}

const DEFAULT_NOTIFICATION_STATUS: NotificationContextState = {
  state: {
    error: null,
    success: null,
    info: null,
    warning: null,
  },
  dispatch: (action: NotificationAction) => {},
}

const reducer = (state: NotificationState, action: NotificationAction) => {
  switch (action.type) {
    case 'ClearAll': {
      return {
        ...DEFAULT_NOTIFICATION_STATUS.state,
      }
    }
    case 'SetError':
      return {
        ...state,
        error: action.payload,
      }
    case 'ClearError': {
      return {
        ...state,
        error: null,
      }
    }
    case 'SetWarning':
      return {
        ...state,
        warning: action.payload,
      }
    case 'ClearWarning': {
      return {
        ...state,
        warning: null,
      }
    }
    case 'SetInfo':
      return {
        ...state,
        info: action.payload,
      }
    case 'ClearInfo': {
      return {
        ...state,
        info: null,
      }
    }
    case 'SetSuccess':
      return {
        ...state,
        success: action.payload,
      }
    case 'ClearSuccess': {
      return {
        ...state,
        success: null,
      }
    }
    default:
      return state
  }
}

export const NotificationsContext = React.createContext<
  NotificationContextState
>(DEFAULT_NOTIFICATION_STATUS)

type NotificationsContextProviderProps = {
  children: any
}

export const NotificationsContextProvider: React.FunctionComponent<NotificationsContextProviderProps> = ({
  children,
}) => {
  const initialState = DEFAULT_NOTIFICATION_STATUS.state
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const value = { state, dispatch }

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export const NotificationsContextConsumer = NotificationsContext.Consumer
